/**
 * Stripe Payment Links Configuration
 *
 * SETUP INSTRUCTIONS:
 * ===================
 * 1. Go to Stripe Dashboard → Products
 * 2. Create products and prices for each plan/country combination
 * 3. Go to Payment Links and create links for each price
 * 4. Copy the Payment Link URLs and paste them below
 * 5. Replace the placeholder URLs with your actual Stripe Payment Link URLs
 *
 * IMPORTANT:
 * - Use TEST mode links for development (https://buy.stripe.com/test_...)
 * - Use LIVE mode links for production (https://buy.stripe.com/...)
 * - Keep test and live configurations separate
 *
 * PAYMENT LINK CONFIGURATION:
 * When creating Payment Links in Stripe Dashboard, configure:
 * - Enable "Prefilled customer email"
 * - Trial period: 14 days
 * - After payment: Redirect to success URL
 * - After cancel: Redirect to pricing page
 */

// ============================================================================
// TEST MODE - Payment Links (Development)
// ============================================================================

const TEST_PAYMENT_LINKS = {
  // United States Plans
  us_monthly: 'https://buy.stripe.com/test_5kQ7sDcI6ga22re49OafS02',   // $5.99/month
  us_yearly: 'https://buy.stripe.com/test_00wcMX5fE0b4gi4aycafS03',    // $65/year

  // United Kingdom Plans
  uk_monthly: 'https://buy.stripe.com/test_aFa14f0Zof5Y3vi8q4afS00',   // £4.99/month
  uk_yearly: 'https://buy.stripe.com/test_fZu28j7nMe1U2re8q4afS01',    // £49.99/year
};

// ============================================================================
// LIVE MODE - Payment Links (Production)
// ============================================================================

const LIVE_PAYMENT_LINKS = {
  // United States Plans
  us_monthly: 'https://buy.stripe.com/fZu28j7nMe1U2re8q4afS01',   // $5.99/month
  us_yearly: 'https://buy.stripe.com/5kQ7sDcI6ga22re49OafS02',    // $65/year

  // United Kingdom Plans
  uk_monthly: 'https://buy.stripe.com/00wcMX5fE0b4gi4aycafS03',   // £4.99/month
  uk_yearly: 'https://buy.stripe.com/14A6ozeQe3ng4zm7m0afS04',    // £49.99/year
};

// ============================================================================
// Configuration
// ============================================================================

/**
 * Determine if we're in production or test mode
 * You can change this based on your environment detection logic
 */
// ✅ SECURITY: Check both HTTPS protocol and hostname
const IS_PRODUCTION = process.env.NODE_ENV === 'production' ||
                      (typeof window !== 'undefined' &&
                       window.location.protocol === 'https:' &&
                       (window.location.hostname === 'planlens.ai' ||
                        window.location.hostname === 'wealth-vision-ai.web.app'));

// ✅ SECURITY: Warn if not using HTTPS in production environments
if (typeof window !== 'undefined' &&
    window.location.protocol !== 'https:' &&
    window.location.hostname !== 'localhost' &&
    !window.location.hostname.includes('127.0.0.1') &&
    !window.location.hostname.includes('.local')) {
  console.warn('⚠️ WARNING: Not using HTTPS! Stripe payments and sensitive data require HTTPS.');
}

/**
 * Cloud Function URLs
 * Update these after deploying Cloud Functions
 */
const CLOUD_FUNCTION_BASE_URL = IS_PRODUCTION
  ? 'https://us-central1-wealth-vision-ai.cloudfunctions.net'  // Your production URL
  : 'https://us-central1-wealth-vision-ai.cloudfunctions.net';  // Same for test mode

/**
 * Success/Cancel URLs
 */
const SUCCESS_URL = IS_PRODUCTION
  ? 'https://planlens.app/profile'
  : 'http://localhost:3000/profile';

const CANCEL_URL = IS_PRODUCTION
  ? 'https://planlens.app/pricing'
  : 'http://localhost:3000/pricing';

// ============================================================================
// Exported Configuration
// ============================================================================

/**
 * Get the appropriate Payment Links based on environment
 */
export const PAYMENT_LINKS = IS_PRODUCTION ? LIVE_PAYMENT_LINKS : TEST_PAYMENT_LINKS;

/**
 * Get Payment Link URL with parameters
 * @param {string} country - 'US' or 'UK'
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @param {string} email - User's email for prefill
 * @param {string} uid - Firebase user UID for client_reference_id
 * @returns {string} Complete Payment Link URL with parameters
 */
export function getPaymentLinkUrl(country, billingPeriod, email, uid) {
  const linkKey = `${country.toLowerCase()}_${billingPeriod}`;
  const baseUrl = PAYMENT_LINKS[linkKey];

  if (!baseUrl || baseUrl.includes('REPLACE_WITH_YOUR_LINK')) {
    console.error(`Payment link not configured for ${linkKey}`);
    throw new Error('Payment link not configured. Please contact support.');
  }

  // Build URL with query parameters
  const url = new URL(baseUrl);
  url.searchParams.set('prefilled_email', email);
  url.searchParams.set('client_reference_id', uid);

  return url.toString();
}

/**
 * Get Cloud Function URL
 * @param {string} functionName - Name of the Cloud Function
 * @returns {string} Complete Cloud Function URL
 */
export function getCloudFunctionUrl(functionName) {
  return `${CLOUD_FUNCTION_BASE_URL}/${functionName}`;
}

/**
 * Get success/cancel URLs
 */
export const REDIRECT_URLS = {
  success: SUCCESS_URL,
  cancel: CANCEL_URL
};

/**
 * Pricing information (for display purposes)
 */
export const PRICING = {
  us: {
    monthly: {
      amount: 5.99,
      currency: 'USD',
      display: '$5.99/month',
      yearlyEquivalent: '$71.88/year'
    },
    yearly: {
      amount: 65,
      currency: 'USD',
      display: '$65/year',
      monthlySavings: '$6.88',
      percentSavings: '10%'
    }
  },
  uk: {
    monthly: {
      amount: 4.99,
      currency: 'GBP',
      display: '£4.99/month',
      yearlyEquivalent: '£59.88/year'
    },
    yearly: {
      amount: 49.99,
      currency: 'GBP',
      display: '£49.99/year',
      monthlySavings: '£9.89',
      percentSavings: '16%'
    }
  }
};

/**
 * Trial period (days)
 */
export const TRIAL_DAYS = 14;

/**
 * Stripe mode detection
 */
export const STRIPE_MODE = IS_PRODUCTION ? 'live' : 'test';

export default {
  PAYMENT_LINKS,
  getPaymentLinkUrl,
  getCloudFunctionUrl,
  REDIRECT_URLS,
  PRICING,
  TRIAL_DAYS,
  STRIPE_MODE,
  IS_PRODUCTION
};
